import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Hammer, Plus, Pencil, Trash2, ClipboardList, CheckCircle2, AlertTriangle, Search, X, History, LayoutList, ChevronDown, ChevronRight, Archive, ArchiveRestore, PanelLeftClose, PanelLeftOpen, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Fabrication", "Repair", "Overhaul", "Inspection", "Modification", "Other"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const BASE_ORDER = { YZF: 0, YEG: 1, YYC: 2 };
const STATUS_META = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500", btn: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  on_hold: { label: "On Hold", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-400", btn: "bg-amber-500 hover:bg-amber-600 text-white" },
  completed: { label: "Completed", cls: "bg-sky-100 text-sky-800", dot: "bg-sky-500", btn: "bg-sky-600 hover:bg-sky-700 text-white" },
};
const PRIORITY_CLS = {
  Urgent: "bg-rose-100 text-rose-800",
  High: "bg-amber-100 text-amber-800",
  Medium: "bg-sky-100 text-sky-800",
  Low: "bg-gray-100 text-gray-600",
};

const todayIso = () => new Date().toLocaleDateString("en-CA");
const isOverdue = (d) => !!d && d < todayIso();
const isDueSoon = (d) => {
  if (!d || isOverdue(d)) return false;
  return d <= new Date(Date.now() + 30 * 864e5).toLocaleDateString("en-CA");
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function MshopStatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

function ProjectFormDialog({ open, onOpenChange, initial, onSaved, cities }) {
  const empty = { name: "", category: "Other", base: "", serial: "", part_number: "", hours: "", next_service_due: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      name: initial.name || "",
      category: initial.category || "Other",
      base: initial.base || "",
      serial: initial.serial || "",
      part_number: initial.part_number || "",
      hours: initial.hours ?? "",
      next_service_due: initial.next_service_due || "",
      notes: initial.notes || "",
    } : empty);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        hours: form.hours === "" ? null : Number(form.hours),
        next_service_due: form.next_service_due || null,
      };
      if (initial) await api.patch(`/mshop/projects/${initial.id}`, body);
      else await api.post("/mshop/projects", body);
      toast.success(initial ? "Project updated" : "Project added");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save project");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mshop-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Hammer className="w-4 h-4" /> {initial ? "Edit project" : "Add project"}</DialogTitle>
          <DialogDescription>{initial ? "Update the project's details below." : "Register a new shop project."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Project name *</Label>
              <Input className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Bench overhaul" data-testid="mshop-form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="mshop-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`mshop-category-${c.toLowerCase().replace(/\s/g, "-")}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Base / location</Label>
              <select
                className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm"
                value={form.base}
                onChange={(e) => set("base", e.target.value)}
                data-testid="mshop-form-base"
              >
                <option value="">— Select base —</option>
                {(cities || []).map((c) => <option key={c.code} value={c.code}>{`${c.code} — ${c.name}`}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Reference / serial #</Label>
              <Input className="mt-1" value={form.serial} onChange={(e) => set("serial", e.target.value)} data-testid="mshop-form-serial" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Part number</Label>
              <Input className="mt-1" value={form.part_number} onChange={(e) => set("part_number", e.target.value)} placeholder="e.g. PN-9987-A" data-testid="mshop-form-part" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Estimated hours</Label>
              <Input className="mt-1" type="number" min="0" step="0.1" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="mshop-form-hours" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Target date</Label>
              <Input className="mt-1" type="date" value={form.next_service_due} onChange={(e) => set("next_service_due", e.target.value)} data-testid="mshop-form-due" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Notes</Label>
            <Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="mshop-form-notes" />
          </div>
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submit} disabled={busy} data-testid="mshop-form-submit">
            {initial ? "Save changes" : "Add project"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColumnHead({ children }) {
  return <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 border-b border-summit-hairline pb-1.5 mb-2">{children}</div>;
}

function ProjectDetails({ eq, onSaved }) {
  const [hours, setHours] = useState(eq.hours ?? "");
  const [due, setDue] = useState(eq.next_service_due || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHours(eq.hours ?? ""); setDue(eq.next_service_due || ""); }, [eq.id, eq.hours, eq.next_service_due]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/mshop/projects/${eq.id}`, {
        hours: hours === "" ? null : Number(hours),
        next_service_due: due || null,
      });
      toast.success("Project info updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <ColumnHead>Project details</ColumnHead>
      <div className="space-y-0.5 text-xs">
        <div className="flex justify-between gap-2"><span className="text-gray-400 text-[10px]">Ref / serial</span><span>{eq.serial || "—"}</span></div>
        <div className="flex justify-between gap-2"><span className="text-gray-400 text-[10px]">Part #</span><span data-testid={`mshop-part-${eq.id}`}>{eq.part_number || "—"}</span></div>
        <div className="flex justify-between gap-2"><span className="text-gray-400 text-[10px]">Category</span><span>{eq.category}</span></div>
        <div className="flex justify-between gap-2"><span className="text-gray-400 text-[10px]">Base</span><span>{eq.base || "—"}</span></div>
        <div className="flex justify-between gap-2"><span className="text-gray-400 text-[10px]">Added by</span><span className="truncate">{eq.created_by_name}</span></div>
        <div className="flex justify-between gap-2">
          <span className="text-gray-400 text-[10px]">Target date</span>
          <span className={isOverdue(eq.next_service_due) ? "text-rose-600 font-medium" : ""} data-testid={`mshop-due-${eq.id}`}>
            {eq.next_service_due || "—"}{isOverdue(eq.next_service_due) ? " · OVERDUE" : ""}
          </span>
        </div>
      </div>
      <div className="mt-2 border border-summit-hairline rounded-md p-1.5 bg-summit-canvas/50 space-y-1">
        <div className="font-mono text-[8px] uppercase tracking-widest text-gray-400">Quick update</div>
        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <Label className="text-[8px] uppercase tracking-widest text-gray-500">Est. hours</Label>
            <Input className="h-6 mt-0.5 text-[10px] px-1.5" type="number" min="0" step="0.1" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="mshop-quick-hours" />
          </div>
          <div>
            <Label className="text-[8px] uppercase tracking-widest text-gray-500">Target date</Label>
            <Input className="h-6 mt-0.5 text-[10px] px-1.5" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="mshop-quick-due" />
          </div>
        </div>
        <Button size="sm" className="h-5 px-2 text-[9px] bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={busy} onClick={save} data-testid="mshop-quick-update">Update</Button>
      </div>
      {eq.notes && <div className="mt-2 text-[11px] bg-white border border-summit-hairline rounded-md p-2 whitespace-pre-wrap" data-testid={`mshop-notes-${eq.id}`}>{eq.notes}</div>}
    </div>
  );
}

function ProjectPhoto({ eq, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);
  const inputRef = React.useRef(null);

  const save = async (photo) => {
    setBusy(true);
    try {
      await api.patch(`/mshop/projects/${eq.id}`, { photo });
      toast.success(photo ? "Photo updated" : "Photo removed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save photo");
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 640 / img.width);
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      save(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(img.src); toast.error("Couldn't read that image"); };
    img.src = URL.createObjectURL(file);
  };

  return (
    <div data-testid={`mshop-photo-section-${eq.id}`}>
      <ColumnHead>Photo</ColumnHead>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`mshop-photo-input-${eq.id}`} />
      {eq.photo ? (
        <div className="relative group w-full max-w-[220px]">
          <img
            src={eq.photo}
            alt={eq.name}
            className="w-full aspect-[4/3] object-cover rounded-md border border-summit-hairline bg-white cursor-zoom-in"
            onClick={() => setViewing(true)}
            title="Click to enlarge"
            data-testid={`mshop-photo-${eq.id}`}
          />
          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button className="p-1 rounded bg-black/50 text-white hover:bg-black/70" disabled={busy} onClick={() => inputRef.current?.click()} title="Replace photo" data-testid={`mshop-photo-replace-${eq.id}`}>
              <Camera className="w-3 h-3" />
            </button>
            <button className="p-1 rounded bg-black/50 text-white hover:bg-rose-600" disabled={busy} onClick={() => save(null)} title="Remove photo" data-testid={`mshop-photo-remove-${eq.id}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <button
          className="w-full max-w-[220px] aspect-[4/3] rounded-md border-2 border-dashed border-summit-hairline bg-white/60 hover:border-summit-primary hover:bg-summit-glacier/30 transition-colors flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-summit-primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`mshop-photo-add-${eq.id}`}
        >
          <Camera className="w-5 h-5" />
          <span className="font-mono text-[9px] uppercase tracking-widest">{busy ? "Saving…" : "Add photo"}</span>
        </button>
      )}
      <Dialog open={viewing} onOpenChange={setViewing}>
        <DialogContent className="max-w-3xl p-2" data-testid={`mshop-photo-lightbox-${eq.id}`}>
          <DialogHeader className="px-2 pt-1">
            <DialogTitle className="font-display text-base">{eq.name}{eq.base ? ` — ${eq.base}` : ""}</DialogTitle>
          </DialogHeader>
          {eq.photo && (
            <img src={eq.photo} alt={eq.name} className="w-full max-h-[75vh] object-contain rounded-md" data-testid={`mshop-photo-large-${eq.id}`} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusSwitcher({ eq, onSaved }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const change = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/mshop/projects/${eq.id}`, { status, status_note: note || null });
      toast.success(`${eq.name} → ${STATUS_META[status].label}`);
      setNote("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change status");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <ColumnHead>Status</ColumnHead>
      <div className="flex flex-wrap gap-1">
        {Object.entries(STATUS_META).map(([key, m]) => (
          <Button key={key} size="sm" className={`h-6 px-2 text-[10px] ${eq.status === key ? "opacity-40 pointer-events-none " : ""}${m.btn}`} disabled={busy} onClick={() => change(key)} data-testid={`mshop-status-${key}`}>
            {m.label}
          </Button>
        ))}
      </div>
      <Input className="mt-1.5 h-6 text-[11px] max-w-xs" placeholder="Status note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="mshop-status-note" />
      {eq.status_changed_by_name && (
        <div className="text-[10px] text-gray-500 mt-1.5" data-testid="mshop-status-history">
          {STATUS_META[eq.status]?.label} since {fmtWhen(eq.status_changed_at)} · {eq.status_changed_by_name}
          {eq.status_note ? <span className="italic"> — “{eq.status_note}”</span> : null}
        </div>
      )}
    </div>
  );
}

function WorkOrders({ eq, orders, members, user, onSaved }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", priority: "Medium", due_date: "", assignee_id: "", affected_area: "", parts_ordered: false });
  const [completing, setCompleting] = useState(null);
  const [doneForm, setDoneForm] = useState({ notes: "", parts: "", labor_hours: "" });
  const [busy, setBusy] = useState(false);
  const open = orders.filter((o) => o.status === "open");

  const create = async () => {
    if (!form.title.trim()) { toast.error("Describe the work needed"); return; }
    setBusy(true);
    try {
      await api.post(`/mshop/projects/${eq.id}/work-orders`, {
        ...form,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || null,
        affected_area: form.affected_area || null,
      });
      toast.success("Work order created");
      setForm({ title: "", priority: "Medium", due_date: "", assignee_id: "", affected_area: "", parts_ordered: false });
      setShowNew(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create work order");
    } finally {
      setBusy(false);
    }
  };

  const togglePartsOrdered = async (wo) => {
    try {
      await api.post(`/mshop/work-orders/${wo.id}/parts-ordered`, { parts_ordered: !wo.parts_ordered });
      toast.success(!wo.parts_ordered ? "Marked parts ordered" : "Marked parts not ordered");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const complete = async (wo) => {
    if (doneForm.labor_hours === "" || Number(doneForm.labor_hours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/mshop/work-orders/${wo.id}/complete`, { ...doneForm, labor_hours: Number(doneForm.labor_hours) });
      toast.success("Work order completed — logged");
      setCompleting(null);
      setDoneForm({ notes: "", parts: "", labor_hours: "" });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (wo) => {
    if (!window.confirm(`Delete work order "${wo.title}"?`)) return;
    try {
      await api.delete(`/mshop/work-orders/${wo.id}`);
      toast.success("Work order deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span>Open work orders ({open.length})</span>
          <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowNew(true)} data-testid="mshop-wo-new-toggle">
            <Plus className="w-3 h-3" /> Add
          </button>
        </span>
      </ColumnHead>
      <div className="space-y-2" data-testid="mshop-wo-list">
        {open.length === 0 && <p className="text-xs text-gray-400">No open work orders.</p>}
        {open.map((wo) => (
          <div key={wo.id} className="border border-summit-hairline rounded-md px-2.5 py-2 bg-white" data-testid={`mshop-wo-${wo.id}`}>
            <div className="flex items-start gap-2">
              <span className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${PRIORITY_CLS[wo.priority]}`}>{wo.priority}</span>
              <span className="text-xs font-medium leading-snug whitespace-pre-wrap">{wo.title}</span>
              {(user.role === "admin" || wo.created_by === user.id) && (
                <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600 shrink-0" onClick={() => remove(wo)} data-testid={`mshop-wo-delete-${wo.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {wo.affected_area && <div className="text-[10px] text-gray-600 mt-1" data-testid={`mshop-wo-area-${wo.id}`}>Area: {wo.affected_area}</div>}
            <div className="text-[10px] mt-1 flex items-center gap-1" data-testid={`mshop-wo-parts-ordered-${wo.id}`}>
              <span>Parts ordered:</span>
              <button
                type="button"
                onClick={() => togglePartsOrdered(wo)}
                title="Click to flip"
                className={`font-bold underline-offset-2 hover:underline ${wo.parts_ordered ? "text-emerald-700" : "text-gray-500"}`}
                data-testid={`mshop-wo-parts-toggle-${wo.id}`}
              >
                {wo.parts_ordered ? "Yes" : "No"}
              </button>
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              {wo.assignee_name ? `→ ${wo.assignee_name}` : "Unassigned"} · by {wo.created_by_name}
              {wo.due_date && (
                <span className={`ml-1 font-mono ${isOverdue(wo.due_date) ? "text-rose-600 font-bold" : ""}`}>
                  · due {wo.due_date}{isOverdue(wo.due_date) ? " OVERDUE" : ""}
                </span>
              )}
            </div>
            <div className="mt-1.5">
              <button className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline" onClick={() => { setCompleting(wo); setDoneForm({ notes: "", parts: "", labor_hours: "" }); }} data-testid={`mshop-wo-complete-${wo.id}`}>
                <CheckCircle2 className="w-3 h-3" /> Complete
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="mshop-wo-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ClipboardList className="w-4 h-4" /> New work order</DialogTitle>
            <DialogDescription>{eq.name}{eq.base ? ` — ${eq.base}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work needed *</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="e.g. fabricate mounting bracket" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="mshop-wo-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Affected area</Label>
              <Input className="mt-1" placeholder="e.g. airframe, workbench 2" value={form.affected_area} onChange={(e) => setForm((f) => ({ ...f, affected_area: e.target.value }))} data-testid="mshop-wo-area" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Priority</Label>
                <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} data-testid="mshop-wo-priority">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Due date</Label>
                <Input className="mt-1" type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="mshop-wo-due" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Assign to</Label>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))} data-testid="mshop-wo-assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between border border-summit-hairline rounded-md px-3 py-2.5">
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts ordered?</Label>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-[10px] uppercase tracking-widest ${form.parts_ordered ? "text-emerald-700 font-bold" : "text-gray-400"}`} data-testid="mshop-wo-parts-ordered-label">
                  {form.parts_ordered ? "Yes" : "No"}
                </span>
                <Switch checked={form.parts_ordered} onCheckedChange={(v) => setForm((f) => ({ ...f, parts_ordered: !!v }))} data-testid="mshop-wo-parts-ordered" />
              </div>
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={create} disabled={busy} data-testid="mshop-wo-submit">Create work order</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completing} onOpenChange={(o) => { if (!o) setCompleting(null); }}>
        <DialogContent className="max-w-md" data-testid={`mshop-wo-complete-form-${completing?.id || ""}`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Complete work order</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{completing?.title}{completing?.affected_area ? ` — ${completing.affected_area}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
              <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 2.5" value={doneForm.labor_hours} onChange={(e) => setDoneForm((f) => ({ ...f, labor_hours: e.target.value }))} data-testid="mshop-wo-done-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work performed / notes</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="What was done?" value={doneForm.notes} onChange={(e) => setDoneForm((f) => ({ ...f, notes: e.target.value }))} data-testid="mshop-wo-done-notes" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts used (optional)</Label>
              <Input className="mt-1" value={doneForm.parts} onChange={(e) => setDoneForm((f) => ({ ...f, parts: e.target.value }))} data-testid="mshop-wo-done-parts" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={() => complete(completing)} data-testid="mshop-wo-done-confirm">Mark complete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WorkLog({ eq, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchPage, setSearchPage] = useState(0);
  const [searchDetail, setSearchDetail] = useState(null);
  const [form, setForm] = useState({ date: todayIso(), work: "", parts: "", labor_hours: "" });
  const [busy, setBusy] = useState(false);
  const log = [...(eq.work_log || [])].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const active = log.filter((l) => !l.archived);
  const archived = log.filter((l) => l.archived);
  const totalHours = active.reduce((n, l) => n + (Number(l.labor_hours) || 0), 0);

  const add = async () => {
    if (!form.work.trim()) { toast.error("Describe the work done"); return; }
    if (form.labor_hours === "" || Number(form.labor_hours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/mshop/projects/${eq.id}/log`, { ...form, labor_hours: Number(form.labor_hours) });
      toast.success("Logged");
      setForm({ date: todayIso(), work: "", parts: "", labor_hours: "" });
      setShowAdd(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log work");
    } finally {
      setBusy(false);
    }
  };

  const setArchived = async (l, val) => {
    try {
      await api.patch(`/mshop/projects/${eq.id}/log/${l.id}`, { archived: val });
      toast.success(val ? "Log entry archived" : "Log entry restored");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update entry");
    }
  };

  const renderEntry = (l) => (
    <div key={l.id} className={`border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white text-xs ${l.archived ? "opacity-60" : ""}`} data-testid={`mshop-log-${l.id}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-gray-400">{l.date}</span>
        <span className="font-medium text-[11px]">{l.by_name}</span>
        {l.labor_hours ? <span className="font-mono text-[10px] text-sky-700">{l.labor_hours} hrs</span> : null}
        {l.work_order_id && <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 rounded">WO</span>}
        {l.archived && <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 bg-gray-100 px-1.5 rounded">Archived</span>}
        <button
          className="ml-auto p-0.5 rounded text-gray-300 hover:text-gray-600 shrink-0 self-center"
          onClick={() => setArchived(l, !l.archived)}
          title={l.archived ? "Restore entry" : "Archive entry"}
          data-testid={`mshop-log-${l.archived ? "unarchive" : "archive"}-${l.id}`}
        >
          {l.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="mt-0.5 whitespace-pre-wrap">{l.work}</div>
      {l.affected_area && <div className="text-[10px] text-gray-500 mt-0.5">Area: {l.affected_area}</div>}
      {l.parts && <div className="text-[10px] text-gray-500 mt-0.5">Parts: {l.parts}</div>}
    </div>
  );

  const q = searchQ.trim().toLowerCase();
  const results = q ? log.filter((l) => [l.work, l.parts, l.by_name, l.date, l.affected_area].some((v) => (v || "").toLowerCase().includes(q))) : [];

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><History className="w-3 h-3" /> Work log ({active.length}{totalHours > 0 ? ` · ${totalHours} hrs` : ""})</span>
          <span className="inline-flex items-center gap-2.5">
            <button className="text-gray-400 hover:text-summit-primary" onClick={() => { setSearchQ(""); setSearchPage(0); setSearchDetail(null); setShowSearch(true); }} title="Search work history" data-testid="mshop-log-search-toggle">
              <Search className="w-3.5 h-3.5" />
            </button>
            <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowAdd(true)} data-testid="mshop-log-add-toggle">
              <Plus className="w-3 h-3" /> Add
            </button>
          </span>
        </span>
      </ColumnHead>
      <div className="space-y-1.5 max-h-[50rem] overflow-y-auto pr-1" data-testid="mshop-log-list">
        {active.length === 0 && archived.length === 0 && <p className="text-xs text-gray-400">No work logged yet.</p>}
        {active.map(renderEntry)}
        {showArchived && archived.map(renderEntry)}
      </div>
      {archived.length > 0 && (
        <button
          className="mt-1.5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-gray-600"
          onClick={() => setShowArchived((v) => !v)}
          data-testid="mshop-log-show-archived"
        >
          <Archive className="w-3 h-3" /> {showArchived ? "Hide" : "Show"} archived ({archived.length})
        </button>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="mshop-log-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><History className="w-4 h-4" /> Add work log entry</DialogTitle>
            <DialogDescription>{eq.name}{eq.base ? ` — ${eq.base}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Date</Label>
                <Input className="mt-1" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="mshop-log-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
                <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 1.5" value={form.labor_hours} onChange={(e) => setForm((f) => ({ ...f, labor_hours: e.target.value }))} data-testid="mshop-log-hours" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Work performed *</Label>
              <Textarea className="mt-1 text-sm" rows={4} placeholder="What was done?" value={form.work} onChange={(e) => setForm((f) => ({ ...f, work: e.target.value }))} data-testid="mshop-log-work" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Parts used (optional)</Label>
              <Input className="mt-1" value={form.parts} onChange={(e) => setForm((f) => ({ ...f, parts: e.target.value }))} data-testid="mshop-log-parts" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="mshop-log-submit">Add entry</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSearch} onOpenChange={(o) => { setShowSearch(o); if (!o) { setSearchDetail(null); setSearchPage(0); } }}>
        <DialogContent className="max-w-lg" data-testid="mshop-log-search-dialog">
          {searchDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2"><History className="w-4 h-4" /> Work details</DialogTitle>
                <DialogDescription>{eq.name} — {searchDetail.date}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3" data-testid="mshop-log-search-detail">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Date</span>{searchDetail.date}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Logged by</span>{searchDetail.by_name}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Hours allocated</span>{searchDetail.labor_hours || "—"}</div>
                  <div><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Source</span>{searchDetail.work_order_id ? "Work order" : "Manual entry"}</div>
                  {searchDetail.affected_area && (
                    <div className="col-span-2"><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Affected area</span>{searchDetail.affected_area}</div>
                  )}
                  {searchDetail.parts && (
                    <div className="col-span-2"><span className="text-gray-400 text-[10px] uppercase tracking-widest block">Parts used</span>{searchDetail.parts}</div>
                  )}
                </div>
                <div>
                  <span className="text-gray-400 text-[10px] uppercase tracking-widest block mb-1">Work performed</span>
                  <div className="border border-summit-hairline rounded-md bg-summit-canvas/40 p-3 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">{searchDetail.work}</div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => setSearchDetail(null)} data-testid="mshop-log-search-back">
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to results
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2"><Search className="w-4 h-4" /> Search work history</DialogTitle>
                <DialogDescription>{eq.name} — searches all {log.length} entries, including archived</DialogDescription>
              </DialogHeader>
              <Input placeholder="Search work, parts, area, person or date…" value={searchQ} onChange={(e) => { setSearchQ(e.target.value); setSearchPage(0); }} autoFocus data-testid="mshop-log-search-input" />
              <div className="space-y-1.5 max-h-[30rem] overflow-y-auto pr-1" data-testid="mshop-log-search-results">
                {q === "" ? (
                  <p className="text-xs text-gray-400">Type to search previous work and details.</p>
                ) : results.length === 0 ? (
                  <p className="text-xs text-gray-400">No matching entries.</p>
                ) : (
                  <>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                      {results.length} match{results.length === 1 ? "" : "es"} — click an entry for full details
                    </p>
                    {results.slice(searchPage * 10, searchPage * 10 + 10).map((l) => (
                      <button
                        key={l.id}
                        className="w-full text-left border border-summit-hairline rounded-md px-2.5 py-1.5 bg-white text-xs hover:bg-summit-glacier/40 transition-colors"
                        onClick={() => setSearchDetail(l)}
                        data-testid={`mshop-log-search-result-${l.id}`}
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono text-[10px] text-gray-400">{l.date}</span>
                          <span className="font-medium text-[11px]">{l.by_name}</span>
                          {l.labor_hours ? <span className="font-mono text-[10px] text-sky-700">{l.labor_hours} hrs</span> : null}
                          {l.work_order_id && <span className="font-mono text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 px-1.5 rounded">WO</span>}
                          {l.archived && <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400 bg-gray-100 px-1.5 rounded">Archived</span>}
                        </div>
                        <div className="mt-0.5 truncate text-gray-700">{l.work}</div>
                        {l.affected_area && <div className="text-[10px] text-gray-500 mt-0.5">Area: {l.affected_area}</div>}
                      </button>
                    ))}
                  </>
                )}
              </div>
              {results.length > 10 && (
                <div className="flex items-center justify-between border-t border-summit-hairline pt-2">
                  <button
                    className="font-mono text-[11px] text-summit-primary hover:underline disabled:opacity-30 disabled:no-underline"
                    disabled={searchPage === 0}
                    onClick={() => setSearchPage((p) => p - 1)}
                    data-testid="mshop-log-search-prev"
                  >
                    ‹ Prev
                  </button>
                  <span className="font-mono text-[10px] text-gray-400">Page {searchPage + 1} of {Math.ceil(results.length / 10)}</span>
                  <button
                    className="font-mono text-[11px] text-summit-primary hover:underline disabled:opacity-30 disabled:no-underline"
                    disabled={searchPage >= Math.ceil(results.length / 10) - 1}
                    onClick={() => setSearchPage((p) => p + 1)}
                    data-testid="mshop-log-search-next"
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProblemReports({ eq, user, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [text, setText] = useState("");
  const [resolving, setResolving] = useState(null);
  const [resolution, setResolution] = useState("");
  const [resolveHours, setResolveHours] = useState("");
  const [busy, setBusy] = useState(false);
  const open = (eq.problem_reports || []).filter((p) => p.status === "open");

  const add = async () => {
    if (!text.trim()) { toast.error("Describe the problem"); return; }
    setBusy(true);
    try {
      await api.post(`/mshop/projects/${eq.id}/problems`, { text });
      toast.success("Problem reported — M-Shop team notified");
      setText("");
      setShowAdd(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to report problem");
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    if (resolveHours === "" || Number(resolveHours) <= 0) { toast.error("Allocated hours are required"); return; }
    setBusy(true);
    try {
      await api.post(`/mshop/projects/${eq.id}/problems/${resolving.id}/resolve`, { resolution, labor_hours: Number(resolveHours) });
      toast.success("Problem resolved — logged");
      setResolving(null);
      setResolution("");
      setResolveHours("");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this problem report?")) return;
    try {
      await api.delete(`/mshop/projects/${eq.id}/problems/${p.id}`);
      toast.success("Problem report deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div>
      <ColumnHead>
        <span className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Problem reports ({open.length})</span>
          <button className="inline-flex items-center gap-1 text-summit-primary hover:underline normal-case tracking-normal" onClick={() => setShowAdd(true)} data-testid="mshop-pr-add-toggle">
            <Plus className="w-3 h-3" /> Report
          </button>
        </span>
      </ColumnHead>
      <div className="space-y-2" data-testid="mshop-pr-list">
        {open.length === 0 && <p className="text-xs text-gray-400">No open problems.</p>}
        {open.map((p) => (
          <div key={p.id} className="border border-rose-200 rounded-md px-2.5 py-2 bg-rose-50/50" data-testid={`mshop-pr-${p.id}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
              <span className="text-xs leading-snug whitespace-pre-wrap">{p.text}</span>
              {(user.role === "admin" || p.reported_by === user.id) && (
                <button className="ml-auto p-0.5 rounded text-gray-300 hover:text-rose-600 shrink-0" onClick={() => remove(p)} data-testid={`mshop-pr-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">{p.reported_by_name} · {fmtWhen(p.created_at)}</div>
            <div className="mt-1.5">
              <button className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline" onClick={() => { setResolving(p); setResolution(""); setResolveHours(""); }} data-testid={`mshop-pr-resolve-${p.id}`}>
                <CheckCircle2 className="w-3 h-3" /> Resolve
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="mshop-pr-form">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-500" /> Report a problem</DialogTitle>
            <DialogDescription>{eq.name}{eq.base ? ` — ${eq.base}` : ""} — the M-Shop team will be notified</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">What&apos;s wrong? *</Label>
              <Textarea className="mt-1 text-sm" rows={4} placeholder="Describe the problem in detail…" value={text} onChange={(e) => setText(e.target.value)} data-testid="mshop-pr-text" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="mshop-pr-submit">Report problem</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolving} onOpenChange={(o) => { if (!o) setResolving(null); }}>
        <DialogContent className="max-w-md" data-testid={`mshop-pr-resolve-form-${resolving?.id || ""}`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Resolve problem</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap">{resolving?.text}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Hours allocated *</Label>
              <Input className="mt-1" type="number" min="0.1" step="0.1" placeholder="e.g. 1.5" value={resolveHours} onChange={(e) => setResolveHours(e.target.value)} data-testid="mshop-pr-resolve-hours" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Details of work done (optional)</Label>
              <Textarea className="mt-1 text-sm" rows={3} placeholder="Resolution details…" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="mshop-pr-resolution" />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={resolve} data-testid="mshop-pr-resolve-confirm">Mark resolved</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectPanel({ eq, orders, members, user, onSaved, onEdit }) {
  const canDelete = user.role === "admin" || !!user.mshop_manage;
  const openCount = orders.filter((o) => o.status === "open").length;
  const openProblems = (eq.problem_reports || []).filter((p) => p.status === "open").length;
  const remove = async () => {
    if (!window.confirm(`Delete "${eq.name}" and all its work history?`)) return;
    try {
      await api.delete(`/mshop/projects/${eq.id}`);
      toast.success("Project deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="border border-summit-hairline rounded-lg overflow-hidden bg-white" data-testid={`mshop-panel-${eq.id}`}>
      <div className="bg-summit-primary text-white px-4 py-2 flex items-center gap-3 flex-wrap">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_META[eq.status]?.dot || "bg-gray-400"}`} />
        <span className="font-display font-semibold">{eq.name}</span>
        {eq.base && <span className="font-mono text-[11px] uppercase tracking-widest opacity-80">— {eq.base}</span>}
        <span className="font-mono text-xs opacity-90 ml-2">{eq.hours ?? "—"} <span className="opacity-60 text-[10px]">est. hrs</span></span>
        <span className={`font-mono text-xs ml-2 ${isOverdue(eq.next_service_due) ? "text-rose-200 font-bold" : "opacity-90"}`}>
          {eq.next_service_due || "—"} <span className="opacity-60 text-[10px]">target date</span>
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest opacity-70 ml-2">{eq.category}{eq.serial ? ` · REF ${eq.serial}` : ""}</span>
        {isOverdue(eq.next_service_due) && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/90 font-mono text-[9px] uppercase" data-testid={`mshop-overdue-${eq.id}`}>
            <AlertTriangle className="w-3 h-3" /> Past target
          </span>
        )}
        {openCount > 0 && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/15 font-mono text-[10px]" data-testid={`mshop-wo-count-${eq.id}`}>
            <ClipboardList className="w-3 h-3" /> {openCount} WO
          </span>
        )}
        {openProblems > 0 && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/90 font-mono text-[10px]" data-testid={`mshop-pr-count-${eq.id}`}>
            <AlertTriangle className="w-3 h-3" /> {openProblems} PR
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <MshopStatusBadge status={eq.status} />
          <button className="p-1.5 rounded hover:bg-white/15" onClick={() => onEdit(eq)} title="Edit details" data-testid={`mshop-edit-${eq.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {canDelete && (
            <button className="p-1.5 rounded hover:bg-white/15" onClick={remove} title="Delete project" data-testid={`mshop-delete-${eq.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-5 p-4 bg-summit-canvas/30">
        <div className="space-y-5">
          <StatusSwitcher eq={eq} onSaved={onSaved} />
          <ProjectPhoto eq={eq} onSaved={onSaved} />
          <ProjectDetails eq={eq} onSaved={onSaved} />
        </div>
        <ProblemReports eq={eq} user={user} onSaved={onSaved} />
        <WorkOrders eq={eq} orders={orders} members={members} user={user} onSaved={onSaved} />
        <WorkLog eq={eq} onSaved={onSaved} />
      </div>
    </div>
  );
}

export default function MShop() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedId, setSelectedId] = useState("all");
  const [query, setQuery] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [collapsedBases, setCollapsedBases] = useState(() => new Set());
  const [collapsedSummary, setCollapsedSummary] = useState(() => new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const hasAccess = user && (user.mshop_access || user.role === "admin");
  const canManage = user && (user.role === "admin" || user.mshop_manage);

  const load = async () => {
    try {
      const [a, b, c, d] = await Promise.all([api.get("/mshop/projects"), api.get("/mshop/work-orders"), api.get("/mshop/members"), api.get("/cities")]);
      setItems(a.data || []);
      setOrders(b.data || []);
      setMembers(c.data || []);
      setCities(d.data || []);
    } catch (e) {
      setItems([]);
      toast.error(e?.response?.data?.detail || "Failed to load M-Shop data");
    }
  };
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const tiles = useMemo(() => {
    const all = items || [];
    return {
      total: all.length,
      active: all.filter((i) => i.status === "active").length,
      on_hold: all.filter((i) => i.status === "on_hold").length,
      completed: all.filter((i) => i.status === "completed").length,
      openWo: orders.filter((o) => o.status === "open").length,
      openPr: all.reduce((n, i) => n + (i.problem_reports || []).filter((p) => p.status === "open").length, 0),
      overdue: all.filter((i) => isOverdue(i.next_service_due)).length,
    };
  }, [items, orders]);

  const sideList = useMemo(() => {
    let list = items || [];
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((i) => [i.name, i.serial, i.notes, i.base, i.category].some((v) => (v || "").toLowerCase().includes(q)));
    return list;
  }, [items, query]);

  const sideGroups = useMemo(() => {
    const map = new Map();
    for (const i of sideList) {
      const key = (i.base || "").toUpperCase() || "NO BASE";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    }
    const prio = (b) => (BASE_ORDER[b] !== undefined ? BASE_ORDER[b] : b === "NO BASE" ? 99 : 50);
    return [...map.entries()].sort(([a], [b]) => prio(a) - prio(b) || a.localeCompare(b));
  }, [sideList]);

  const panels = useMemo(() => {
    if (selectedId === "all") return sideList;
    const one = (items || []).find((i) => i.id === selectedId);
    return one ? [one] : [];
  }, [items, sideList, selectedId]);

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="mshop-no-access">
        <Hammer className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">M-Shop</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  const STAT_CHIPS = [
    ["total", "projects", "text-gray-600"],
    ["active", "active", "text-emerald-700"],
    ["on_hold", "on hold", "text-amber-700"],
    ["completed", "completed", "text-sky-700"],
    ["openWo", "open WOs", "text-sky-700"],
    ["openPr", "open problems", "text-rose-700"],
    ["overdue", "past target", "text-rose-700"],
  ];

  return (
    <div className="flex" style={{ minHeight: "calc(100vh - 56px)" }}>
      {/* Side nav — project list */}
      <aside className={`hidden md:flex flex-col shrink-0 bg-white border-r border-summit-hairline ${sidebarOpen ? "w-44 lg:w-52 xl:w-60" : "w-10"}`} data-testid="mshop-sidebar">
        {!sidebarOpen ? (
          <button className="p-2.5 flex justify-center hover:bg-summit-glacier/40" onClick={() => setSidebarOpen(true)} title="Show project list" data-testid="mshop-sidebar-expand">
            <PanelLeftOpen className="w-4 h-4 text-gray-500" />
          </button>
        ) : (
        <>
        <div className="p-3 border-b border-summit-hairline">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2 flex items-center justify-between">
            <span>Projects</span>
            <button className="p-0.5 rounded hover:bg-summit-glacier/60 text-gray-400 hover:text-gray-600" onClick={() => setSidebarOpen(false)} title="Hide project list" data-testid="mshop-sidebar-collapse">
              <PanelLeftClose className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="h-8 pl-8 text-xs" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="mshop-search" />
            {query && <button className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setQuery("")} data-testid="mshop-search-clear"><X className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <button
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedId === "all" ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
            onClick={() => setSelectedId("all")}
            data-testid="mshop-side-all"
          >
            <LayoutList className="w-3.5 h-3.5 text-gray-400" /> All Projects
            <span className="ml-auto font-mono text-[10px] text-gray-400">{(items || []).length}</span>
          </button>
          {sideGroups.map(([base, units]) => {
            const isCollapsed = !query.trim() && collapsedBases.has(base);
            return (
            <div key={base} data-testid={`mshop-side-group-${base.replace(/\s/g, "-")}`}>
              <button
                className="w-full px-3 pt-2.5 pb-1 font-mono text-[9px] uppercase tracking-widest text-gray-400 flex items-center gap-1.5 hover:text-gray-600"
                onClick={() => setCollapsedBases((s) => { const n = new Set(s); if (n.has(base)) n.delete(base); else n.add(base); return n; })}
                data-testid={`mshop-side-group-toggle-${base.replace(/\s/g, "-")}`}
              >
                {isCollapsed ? <ChevronRight className="w-3 h-3 shrink-0" /> : <ChevronDown className="w-3 h-3 shrink-0" />}
                {base} <span className="text-gray-300">· {units.length}</span>
                {isCollapsed && units.some((u) => isOverdue(u.next_service_due))
                  ? <AlertTriangle className="w-3 h-3 text-rose-500" />
                  : isCollapsed && units.some((u) => isDueSoon(u.next_service_due))
                  ? <AlertTriangle className="w-3 h-3 text-amber-500" />
                  : null}
              </button>
              {!isCollapsed && units.map((i) => (
                <button
                  key={i.id}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-l-2 ${selectedId === i.id ? "border-summit-primary bg-summit-glacier/60 font-medium" : "border-transparent hover:bg-summit-glacier/40"}`}
                  onClick={() => setSelectedId(i.id)}
                  data-testid={`mshop-side-item-${i.id}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_META[i.status]?.dot || "bg-gray-400"}`} />
                  <span className="truncate">{i.name}</span>
                  {isOverdue(i.next_service_due) ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" title="Past target date" data-testid={`mshop-side-overdue-${i.id}`} />
                  ) : isDueSoon(i.next_service_due) ? (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Target within 30 days" data-testid={`mshop-side-duesoon-${i.id}`} />
                  ) : null}
                </button>
              ))}
            </div>
          );})}
          {items !== null && sideList.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No matches.</p>}
        </div>
        {canManage && (
          <div className="p-3 border-t border-summit-hairline">
            <Button size="sm" className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => { setEditItem(null); setOpenForm(true); }} data-testid="mshop-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Add project
            </Button>
          </div>
        )}
        </>
        )}
      </aside>

      {/* Right — working area */}
      <main className="flex-1 min-w-0 px-4 sm:px-6 py-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight" data-testid="mshop-heading">M-Shop</h1>
            <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap" data-testid="mshop-tiles">
              {STAT_CHIPS.map(([k, label, cls]) => (
                <span key={k} className={`font-mono text-[11px] ${cls}`}>
                  <span className="font-bold" data-testid={`mshop-tile-${k}`}>{tiles[k]}</span> {label}
                </span>
              ))}
            </div>
          </div>
          {canManage && (
            <Button className="md:hidden bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => { setEditItem(null); setOpenForm(true); }} data-testid="mshop-new-btn-mobile">
              <Plus className="w-4 h-4 mr-1.5" /> Add
            </Button>
          )}
        </div>

        {/* Mobile project picker (sidebar hidden < md) */}
        <select
          className="md:hidden mt-3 h-9 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          data-testid="mshop-mobile-select"
        >
          <option value="all">All Projects</option>
          {sideGroups.map(([base, units]) => (
            <optgroup key={base} label={base}>
              {units.map((i) => <option key={i.id} value={i.id}>{i.name}{isOverdue(i.next_service_due) ? " ⚠" : ""}</option>)}
            </optgroup>
          ))}
        </select>

        <div className="mt-4 space-y-5">
          {items === null ? (
            <div className="text-center text-gray-400 text-sm py-12">Loading…</div>
          ) : (selectedId === "all" ? sideGroups.length === 0 : panels.length === 0) ? (
            <div className="text-center text-gray-400 text-sm py-12 border border-dashed border-summit-hairline rounded-lg" data-testid="mshop-empty">
              {(items || []).length === 0 ? "No projects yet — add the first project." : "Nothing matches the current search."}
            </div>
          ) : selectedId === "all" ? (
            sideGroups.map(([base, units]) => {
              const city = cities.find((c) => c.code === base);
              const stats = units.map((u) => ({
                u,
                hours: Math.round((u.work_log || []).reduce((n, l) => n + (Number(l.labor_hours) || 0), 0) * 10) / 10,
                pending: orders.filter((o) => o.project_id === u.id && o.status === "open").length,
                completed: (u.work_log || []).length,
              }));
              const tot = stats.reduce((a, s) => ({
                hours: Math.round((a.hours + s.hours) * 10) / 10,
                pending: a.pending + s.pending,
                completed: a.completed + s.completed,
              }), { hours: 0, pending: 0, completed: 0 });
              return (
                <div key={base} className="border border-summit-hairline rounded-lg overflow-hidden bg-white" data-testid={`mshop-summary-group-${base.replace(/\s/g, "-")}`}>
                  <div
                    className="bg-summit-primary text-white px-4 py-2 flex items-center gap-3 flex-wrap cursor-pointer select-none"
                    onClick={() => setCollapsedSummary((s) => { const n = new Set(s); if (n.has(base)) n.delete(base); else n.add(base); return n; })}
                    data-testid={`mshop-summary-toggle-${base.replace(/\s/g, "-")}`}
                  >
                    {(!query.trim() && collapsedSummary.has(base))
                      ? <ChevronRight className="w-4 h-4 shrink-0 opacity-80" />
                      : <ChevronDown className="w-4 h-4 shrink-0 opacity-80" />}
                    <span className="font-display font-semibold">{base}</span>
                    {city && <span className="font-mono text-[11px] uppercase tracking-widest opacity-80">— {city.name}</span>}
                    <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">{units.length} project{units.length === 1 ? "" : "s"}</span>
                    <span className="ml-auto flex items-center gap-4 font-mono text-xs">
                      <span>{tot.hours} <span className="opacity-60 text-[9px] uppercase">hrs</span></span>
                      <span>{tot.pending} <span className="opacity-60 text-[9px] uppercase">pending</span></span>
                      <span>{tot.completed} <span className="opacity-60 text-[9px] uppercase">completed</span></span>
                    </span>
                  </div>
                  {!(!query.trim() && collapsedSummary.has(base)) && (
                  <>
                  <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_80px] sm:grid-cols-[minmax(0,1fr)_100px_100px_100px] px-4 py-1.5 border-b border-summit-hairline font-mono text-[9px] uppercase tracking-widest text-gray-400">
                    <span>Project</span><span className="text-right">Alloc. hrs</span><span className="text-right">Pending</span><span className="text-right">Completed</span>
                  </div>
                  {stats.map(({ u, hours, pending, completed }) => (
                    <button
                      key={u.id}
                      className="w-full grid grid-cols-[minmax(0,1fr)_72px_72px_80px] sm:grid-cols-[minmax(0,1fr)_100px_100px_100px] items-center px-4 py-2 text-sm border-b border-summit-hairline last:border-b-0 hover:bg-summit-glacier/40 text-left transition-colors"
                      onClick={() => setSelectedId(u.id)}
                      data-testid={`mshop-summary-row-${u.id}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_META[u.status]?.dot || "bg-gray-400"}`} />
                        <span className="truncate font-medium">{u.name}</span>
                        {isOverdue(u.next_service_due)
                          ? <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                          : isDueSoon(u.next_service_due)
                          ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          : null}
                        {(u.problem_reports || []).some((p) => p.status === "open") && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-rose-700 bg-rose-50 px-1.5 rounded shrink-0">PR</span>
                        )}
                      </span>
                      <span className="text-right font-mono text-xs">{hours || "—"}</span>
                      <span className={`text-right font-mono text-xs ${pending > 0 ? "text-amber-700 font-bold" : "text-gray-400"}`}>{pending}</span>
                      <span className="text-right font-mono text-xs text-gray-600">{completed}</span>
                    </button>
                  ))}
                  </>
                  )}
                </div>
              );
            })
          ) : (
            panels.map((eq) => (
              <ProjectPanel
                key={eq.id}
                eq={eq}
                orders={orders.filter((o) => o.project_id === eq.id)}
                members={members}
                user={user}
                onSaved={load}
                onEdit={(it) => { setEditItem(it); setOpenForm(true); }}
              />
            ))
          )}
        </div>
      </main>

      <ProjectFormDialog
        open={openForm}
        onOpenChange={(o) => { setOpenForm(o); if (!o) setEditItem(null); }}
        initial={editItem}
        onSaved={load}
        cities={cities}
      />
    </div>
  );
}
